/**
 * The installed string at a well (GET /well-servicing/wells/:id/string,
 * fieldiq-api#1286, WS-3): every non-voided string event on every job at the
 * well, folded in order — never stored. `as of` folds through a day.
 *
 * WHEN THE BASELINE IS NOT KNOWN, SAY SO. If a pull took more than the record
 * shows was ever run (the string went in before FieldIQ), `baselineKnown` is
 * false: the list below is only what the record shows, and the screen says so
 * in words above it, naming each unrecorded pull. Absent values render `—`,
 * never 0; totals are the API's (lengths in metres, as it sent them).
 */
import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useWsWellString } from '../../hooks/useWsString';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { fmtWsNumber, withUnit, wsDay, wsEntered, wsItemIdentity } from '../../lib/well-servicing';
import type { WsInstalledLot } from '../../types/well-servicing';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { WS_DATE_INPUT, WsEmpty, WsField, WsLoading, WsPanel, WsSpec } from './WsBits';

/** `1,200 to 1,250 m` from the lot's numbers and its depth unit; nothing known → `—`. */
export function lotDepth(l: Pick<WsInstalledLot, 'topDepth' | 'bottomDepth' | 'depthUnit'>): string {
  if (l.topDepth === null && l.bottomDepth === null) return '—';
  const end = (n: number | null) => (n === null ? '—' : fmtWsNumber(n));
  return `${end(l.topDepth)} to ${end(l.bottomDepth)}${l.depthUnit ? ` ${l.depthUnit}` : ''}`;
}

export function WellStringPanel({ wellId, today }: { wellId: string; today: string | null }) {
  const [asOf, setAsOf] = useState('');
  const query = useWsWellString(wellId, asOf || null);
  const fold = query.data?.data ?? null;
  const failure = queryLoadFailure(query);
  const totals = fold ? Object.entries(fold.totalsByKind ?? {}) : [];
  const kindLabel = (k: string) => fold?.installed.find((l) => l.componentKind === k)?.componentKindLabel ?? k.replace(/_/g, ' ');

  return (
    <WsPanel eyebrow="String" title="Installed string" testId="ws-well-string">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-3">
        <p className="max-w-3xl text-sm text-fieldiq-muted">
          What is in the well, worked out from every string event on every job here. Voided events are left out.
        </p>
        <WsField label="As of" hint="Blank for every event">
          <input type="date" className={WS_DATE_INPUT} aria-label="String as of" max={today ?? undefined} value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </WsField>
      </div>
      {query.isLoading ? (
        <WsLoading label="Loading the installed string" />
      ) : failure || !fold ? (
        <div className="px-4 py-4">
          <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="the installed string" />
        </div>
      ) : (
        <div className="space-y-4 px-4 py-4">
          {!fold.baselineKnown && (
            <div role="status" className="rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-3 py-2 text-sm text-fieldiq-text" data-testid="ws-string-baseline-unknown">
              <p className="flex items-center gap-2 font-bold">
                <AlertTriangle className="size-4 text-fi-amber" aria-hidden="true" />
                What was in this well before FieldIQ is not known.
              </p>
              <p className="mt-1">
                A pull took out more than the record shows was ever run, so the list below is only what has been recorded here, not the whole string.
              </p>
              {fold.unrecordedPulls.length > 0 && (
                <ul className="mt-1 list-disc pl-5">
                  {fold.unrecordedPulls.map((p) => (
                    <li key={p.pulledEventId}>
                      {wsDay(p.workDate)} · {p.jobNumber || '—'} · {p.componentKindLabel ?? p.componentKind} × {fmtWsNumber(p.quantity)} ·{' '}
                      {wsItemIdentity(p)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <WsSpec label="As of" value={fold.asOf ? wsDay(fold.asOf) : 'Every event'} />
            <WsSpec label="Last event" value={fold.lastEvent ? `${wsDay(fold.lastEvent.workDate)} · ${fold.lastEvent.jobNumber || '—'}` : '—'} />
            <WsSpec label="Events folded" value={fmtWsNumber(fold.eventsFolded)} mono />
            <WsSpec label="Voided, left out" value={fmtWsNumber(fold.voidedSkipped)} mono />
            <WsSpec label="Laid down" value={fmtWsNumber(fold.dispositions?.laidDown)} mono />
            <WsSpec label="Sent for repair" value={fmtWsNumber(fold.dispositions?.sentForRepair)} mono />
            <WsSpec label="Inspected" value={fmtWsNumber(fold.dispositions?.inspected)} mono />
            {totals.map(([k, t]) => (
              <WsSpec key={k} label={kindLabel(k)} value={`${fmtWsNumber(t.quantity)} · ${withUnit(t.lengthM, 'm')}`} mono />
            ))}
          </dl>
          {fold.fromDraftTours > 0 && (
            <p className="text-sm text-fieldiq-muted" data-testid="ws-string-from-drafts">
              {fold.fromDraftTours === 1 ? 'One of these events is' : `${fold.fromDraftTours} of these events are`} on a tour sheet not yet submitted.
            </p>
          )}
          {fold.installed.length === 0 ? (
            <WsEmpty>{fold.eventsFolded === 0 ? 'No string event has been recorded at this well.' : 'The record shows nothing left in the well.'}</WsEmpty>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-left text-sm" data-testid="ws-installed-lots">
                <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                  <tr>
                    <th className="px-3 py-2">Component</th>
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2 text-right">In the well</th>
                    <th className="px-3 py-2 text-right">Length</th>
                    <th className="px-3 py-2">Depth</th>
                    <th className="px-3 py-2">Condition</th>
                    <th className="px-3 py-2">Owner</th>
                    <th className="px-3 py-2">Ran</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-fieldiq-border text-fieldiq-text">
                  {fold.installed.map((l) => (
                    <tr key={l.ranEventId} data-testid="ws-installed-lot">
                      <td className="px-3 py-2 font-bold text-white">{l.componentKindLabel ?? l.componentKind}</td>
                      <td className="px-3 py-2">{wsItemIdentity(l)}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {fmtWsNumber(l.quantity)}
                        {l.partiallyPulled ? ` of ${fmtWsNumber(l.ranQuantity)}` : ''}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{wsEntered(l.length)}</td>
                      <td className="px-3 py-2 font-mono">{lotDepth(l)}</td>
                      <td className="px-3 py-2">{l.conditionLabel || '—'}</td>
                      <td className="px-3 py-2">{l.ownerLabel || l.owner}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {wsDay(l.ranOn)} · {l.jobNumber || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </WsPanel>
  );
}
